/**
 * Actor v3 LangChain Chat API用Reactフック
 * Actor v3のLangChain統合を使用したチャット機能の状態管理とAPI呼び出しを提供
 */

import { useState, useCallback, useRef, useEffect } from "react"
import { actorV3ChatClient, type ChatMessage, type ChatRequest } from "@/lib/api/chat-client"

export interface UseGftdChatOptions {
  initialMessages?: ChatMessage[]
  model?: string
  temperature?: number
  maxTokens?: number
}

export interface UseGftdChatReturn {
  messages: ChatMessage[]
  isLoading: boolean
  error: string | null
  isConnected: boolean
  sendMessage: (content: string) => Promise<void>
  clearMessages: () => void
  retryLastMessage: () => Promise<void>
  setMessages: (messages: ChatMessage[]) => void
}

/**
 * GFTD Chat APIを使用するためのカスタムフック
 * @param options チャット設定オプション
 * @returns チャット機能のインターフェース
 */
export function useGftdChat(options: UseGftdChatOptions = {}): UseGftdChatReturn {
  const {
    initialMessages = [],
    model = "gpt-3.5-turbo",
    temperature = 0.7,
    maxTokens = 1000,
  } = options

  const [messages, setMessages] = useState<ChatMessage[]>(initialMessages)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const lastUserMessageRef = useRef<string>("")

  // API接続状態を定期的にチェック
  useEffect(() => {
    const checkConnection = async () => {
      try {
        const connected = await actorV3ChatClient.healthCheck()
        setIsConnected(connected)
      } catch (error) {
        setIsConnected(false)
      }
    }

    // 初回チェック
    checkConnection()

    // 5分ごとにヘルスチェック（頻度を下げてエラーログを減らす）
    const interval = setInterval(checkConnection, 300000)

    return () => clearInterval(interval)
  }, [])

  /**
   * ユーザーメッセージを送信してAIからの返答を取得
   * @param content ユーザーのメッセージ内容
   */
  const sendMessage = useCallback(async (content: string) => {
    if (!content.trim()) return
    if (isLoading) return

    setError(null)
    setIsLoading(true)
    lastUserMessageRef.current = content

    // ユーザーメッセージを追加
    const userMessage: ChatMessage = {
      id: `user-${Date.now()}`,
      role: "user",
      content: content.trim(),
      timestamp: new Date(),
    }

    setMessages(prevMessages => [...prevMessages, userMessage])

    try {
      // Actor v3 LangChain統合のAPI呼び出し
      const response = await actorV3ChatClient.chat(content, "default-conversation")

      // レスポンスの安全性確認
      if (!response || typeof response.content !== 'string') {
        throw new Error("APIから無効な応答が返されました")
      }

      const assistantMessage: ChatMessage = {
        id: `assistant-${Date.now()}`,
        role: "assistant",
        content: response.content,
        timestamp: new Date(),
      }

      setMessages(prevMessages => [...prevMessages, assistantMessage])
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : "メッセージの送信に失敗しました"
      setError(errorMessage)

      // ネットワークエラーは静かに処理
      if (errorMessage.includes('fetch') || errorMessage.includes('network')) {
        console.warn("チャットAPI接続エラー:", errorMessage)
      } else {
        console.error("チャットメッセージ送信エラー:", error)
        
        // エラーメッセージを表示
        const errorChatMessage: ChatMessage = {
          id: `error-${Date.now()}`,
          role: "assistant",
          content: `❌ エラー: ${errorMessage}`,
          timestamp: new Date(),
        }

        setMessages(prevMessages => [...prevMessages, errorChatMessage])
      }
    } finally {
      setIsLoading(false)
    }
  }, [messages, isLoading, model, temperature, maxTokens])

  /**
   * 最後のユーザーメッセージを再送信
   */
  const retryLastMessage = useCallback(async () => {
    if (lastUserMessageRef.current && !isLoading) {
      await sendMessage(lastUserMessageRef.current)
    }
  }, [sendMessage, isLoading])

  /**
   * メッセージ履歴をクリア
   */
  const clearMessages = useCallback(() => {
    setMessages([])
    setError(null)
    lastUserMessageRef.current = ""
  }, [])

  return {
    messages,
    isLoading,
    error,
    isConnected,
    sendMessage,
    clearMessages,
    retryLastMessage,
    setMessages,
  }
} 